import { z } from 'zod';
import type { PlayerId, RoomCode } from './ids';

/**
 * Free text is allowed in rooms that are joined with a code ("Kun med kode"), where the
 * players know each other (ADR 0008). In open rooms, the host writes freely and lets
 * others write when they ask (ADR 0011). Everyone else has the game's fixed messages.
 */
export const CHAT_MAX_LENGTH = 60;

export interface ChatSendRequest {
  text: string;
}

/** Why the server did not pass a message on. */
export type ChatRejection =
  'notAllowed' | 'rateLimited' | 'empty' | 'tooLong' | 'contact' | 'language';

export type ChatSendResponse =
  { ok: true } | { ok: false; error: ChatRejection | 'INVALID_REQUEST' };

/** A message as everyone in the room sees it. */
export interface ChatMessage {
  playerId: PlayerId;
  displayName: string;
  text: string;
  sentAt: number;
}

/** What the admin sees: every message, also the ones that were stopped. */
export interface ChatLogEntry {
  sentAt: number;
  room: RoomCode;
  playerId: PlayerId;
  username: string;
  displayName: string;
  text: string;
  /** `null` when the message was delivered. */
  rejected: ChatRejection | null;
  /** The host who let the player write in an open room (their user name), or `null`. */
  approvedBy: string | null;
}

/**
 * Who may write freely in an open room (ADR 0011). The host is the player who has been
 * in the room the longest, so it is whoever created it until they leave.
 */
export interface ChatAccess {
  hostId: PlayerId | null;
  /** Players the host has let write. The host is not in the list. */
  allowed: PlayerId[];
}

/** A player who asks the host to write freely. */
export interface ChatAccessRequest {
  playerId: PlayerId;
  displayName: string;
}

export type ChatRequestResponse =
  { ok: true } | { ok: false; error: 'notNeeded' | 'pending' | 'tooSoon' | 'INVALID_REQUEST' };

/** The host's answer to a request, or taking a permission back (`allow: false`). */
export interface ChatDecisionRequest {
  playerId: PlayerId;
  allow: boolean;
}

export type ChatDecisionResponse =
  { ok: true } | { ok: false; error: 'notHost' | 'notFound' | 'INVALID_REQUEST' };

/** What the asking player hears back. */
export type ChatRequestOutcome = 'allowed' | 'denied' | 'expired' | 'revoked';

/** Generous on the wire; the chat policy decides what is too long after tidying the text. */
export const chatSendRequestSchema = z.object({ text: z.string().max(500) });

export const chatDecisionRequestSchema = z.object({
  playerId: z.string().min(1).max(64),
  allow: z.boolean(),
});
